import { useCallback, useEffect, useState } from 'react';
import styles from './ATMController.module.css';
import { authService } from '../../services/auth.service';
import { getAllRoles } from '../../services/roles.service';
import { getAllUsersService, User } from '../../services/getAllUsers.service';
import { createUsersService } from '../../services/createUsers.service';
import { formatDateTime } from './format';

const ATM_MANAGER_ROLE = 'ATM_MANAGER';

const initialForm = { username: '', name: '', password: '' };

/**
 * Admin-only: lists ATM_MANAGER users and creates new ones. An ATM manager
 * logs into the back office and sees only the ATM Controller, across every
 * terminal.
 */
const AtmManagersModal = ({ onClose }: { onClose: () => void }) => {
    const [managers, setManagers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState(initialForm);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const loadManagers = useCallback(async () => {
        setLoading(true);
        try {
            const roles = await getAllRoles();
            const roleId = roles.find((r) => r.name === ATM_MANAGER_ROLE)?.id;
            if (!roleId) {
                setError('The ATM_MANAGER role does not exist yet - run the latest migration.');
                setManagers([]);
                return;
            }
            const data = await getAllUsersService.getAllUsers(roleId, 100);
            setManagers(data.users);
        } catch (err: any) {
            setError(err?.message || 'Failed to load ATM managers');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadManagers();
    }, [loadManagers]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);
        setSubmitting(true);

        const currentUser = authService.getStoredUser();
        try {
            const created = await createUsersService.createUser({
                username: form.username,
                name: form.name,
                password: form.password,
                role: ATM_MANAGER_ROLE,
                host: currentUser?.environment?.host || 'flow888.app',
                parentId: currentUser?.id,
            });
            setSuccess(`ATM manager "${created.username}" created`);
            setForm(initialForm);
            loadManagers();
        } catch (err: any) {
            setError(
                err?.graphQLErrors?.[0]?.message || err?.message || 'Failed to create ATM manager',
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className={styles.modal} onClick={onClose}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>ATM managers</h2>
                    <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">
                        ×
                    </button>
                </div>

                <p className={styles.detailMuted}>
                    ATM managers sign in to the back office and see only the ATM
                    Controller, with full control over every terminal.
                </p>

                <div className={styles.managerList}>
                    {loading ? (
                        <span className={styles.detailMuted}>Loading…</span>
                    ) : managers.length === 0 ? (
                        <span className={styles.detailMuted}>No ATM managers yet.</span>
                    ) : (
                        managers.map((manager) => (
                            <div key={manager.id} className={styles.managerRow}>
                                <div>
                                    <div className={styles.atmName}>{manager.name || manager.username}</div>
                                    <div className={styles.atmSub}>
                                        @{manager.username}
                                        {manager.isActive ? '' : ' · inactive'}
                                    </div>
                                </div>
                                <div className={styles.atmSub}>
                                    {manager.lastLogin
                                        ? `last login ${formatDateTime(manager.lastLogin)}`
                                        : 'never logged in'}
                                </div>
                            </div>
                        ))
                    )}
                </div>

                <form className={styles.form} onSubmit={handleSubmit}>
                    <div className={styles.formGroup}>
                        <label className={styles.formLabel} htmlFor="atm-manager-username">Username</label>
                        <input
                            id="atm-manager-username"
                            name="username"
                            className={styles.formInput}
                            value={form.username}
                            onChange={handleChange}
                            autoComplete="off"
                            required
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <label className={styles.formLabel} htmlFor="atm-manager-name">Name</label>
                        <input
                            id="atm-manager-name"
                            name="name"
                            className={styles.formInput}
                            value={form.name}
                            onChange={handleChange}
                            required
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <label className={styles.formLabel} htmlFor="atm-manager-password">Password</label>
                        <input
                            id="atm-manager-password"
                            name="password"
                            type="password"
                            className={styles.formInput}
                            value={form.password}
                            onChange={handleChange}
                            autoComplete="new-password"
                            required
                        />
                    </div>

                    {error && <div className={styles.error}>{error}</div>}
                    {success && <div className={styles.success}>{success}</div>}

                    <div className={styles.formActions}>
                        <button type="button" className={styles.cancelBtn} onClick={onClose}>
                            Close
                        </button>
                        <button type="submit" className={styles.submitBtn} disabled={submitting}>
                            {submitting ? 'Creating…' : 'Create ATM manager'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AtmManagersModal;
